import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  ArrowLeft,
  Building2,
  DollarSign,
  Calendar,
  Loader2,
  FileText,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  Clock,
  Download,
  Plus,
  TrendingUp,
  TrendingDown,
  Banknote,
  Receipt,
  Trash2,
  XCircle,
  FileSpreadsheet,
  ExternalLink,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminReportes() {
  const [activeTab, setActiveTab] = useState('estado-cuenta');
  const [clinicas, setClinicas] = useState([]);
  const [selectedClinica, setSelectedClinica] = useState('');
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [antiguedadSaldos, setAntiguedadSaldos] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAbonoDialog, setShowAbonoDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [fifoPreview, setFifoPreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [userPermisos, setUserPermisos] = useState(null);
  
  // Anular abono states
  const [showAnularDialog, setShowAnularDialog] = useState(false);
  const [abonoToAnular, setAbonoToAnular] = useState(null);
  const [anularMotivo, setAnularMotivo] = useState('');
  const [isAnulando, setIsAnulando] = useState(false);
  
  // Ajuste detail modal
  const [showAjusteDialog, setShowAjusteDialog] = useState(false);
  const [ajusteDetail, setAjusteDetail] = useState(null);
  
  // Anular ajuste states
  const [showAnularAjusteDialog, setShowAnularAjusteDialog] = useState(false);
  const [ajusteToAnular, setAjusteToAnular] = useState(null);
  const [anularAjusteMotivo, setAnularAjusteMotivo] = useState('');
  const [isAnulandoAjuste, setIsAnulandoAjuste] = useState(false);
  
  const [abonoForm, setAbonoForm] = useState({
    monto: '',
    tipo: 'transferencia',
    referencia: '',
    fecha: new Date().toISOString().split('T')[0],
  });
  
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  // Permission helpers
  const canExport = () => userPermisos?.reportes?.includes('exportar');
  const canRegistrarAbono = () => userPermisos?.reportes?.includes('registrar_abono');
  const canAnularAbono = () => userPermisos?.reportes?.includes('anular_abono');
  const canAnularAjuste = () => userPermisos?.reportes?.includes('anular_ajuste');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchUserPermisos();
    fetchClinicas();
    fetchAntiguedadSaldos();
  }, [token, navigate]);

  const fetchUserPermisos = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/me`, { headers });
      const permisos = response.data.permisos;
      setUserPermisos(permisos);
      
      // If user doesn't have reportes.ver permission, redirect
      if (!permisos.reportes || !permisos.reportes.includes('ver')) {
        toast.error('No tiene permiso para acceder a Reportes');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Error fetching user permissions:', error);
    }
  };

  const fetchClinicas = async () => {
    try {
      const response = await axios.get(`${API}/admin/clinicas`, { headers });
      // Solo mostrar clínicas con forma de cobro "cotizacion" (crédito)
      const creditClinicas = response.data.filter(c => c.forma_cobro === 'cotizacion' && c.activo);
      setClinicas(creditClinicas);
    } catch (error) {
      console.error('Error fetching clinicas:', error);
      if (error.response?.status === 401) navigate('/admin/login');
      if (error.response?.status === 403) {
        toast.error('No tiene permiso para ver clínicas');
        navigate('/admin/dashboard');
      }
    }
  };

  const fetchEstadoCuenta = async (clinicaId) => {
    if (!clinicaId) return;
    setIsLoading(true);
    try {
      let url = `${API}/reportes/estado-cuenta/${clinicaId}`;
      const params = new URLSearchParams();
      if (fechaInicio) params.append('fecha_inicio', fechaInicio);
      if (fechaFin) params.append('fecha_fin', fechaFin);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { headers });
      setEstadoCuenta(response.data);
    } catch (error) {
      console.error('Error fetching estado cuenta:', error);
      toast.error('Error al cargar estado de cuenta');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAntiguedadSaldos = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/reportes/antiguedad-saldos`, { headers });
      setAntiguedadSaldos(response.data);
    } catch (error) {
      console.error('Error fetching antiguedad saldos:', error);
      toast.error('Error al cargar antigüedad de saldos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClinicaChange = (clinicaId) => {
    setSelectedClinica(clinicaId);
    if (clinicaId) {
      fetchEstadoCuenta(clinicaId);
    } else {
      setEstadoCuenta(null);
    }
  };

  const handlePreviewAbono = async () => {
    if (!selectedClinica || !abonoForm.monto) {
      toast.error('Seleccione una clínica e ingrese un monto');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/abonos/preview`, {
        clinica_id: selectedClinica,
        monto: parseFloat(abonoForm.monto)
      }, { headers });
      setFifoPreview(response.data);
      setShowPreviewDialog(true);
    } catch (error) {
      console.error('Error preview abono:', error);
      toast.error('Error al previsualizar abono');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAbono = async () => {
    if (!selectedClinica || !abonoForm.monto || !abonoForm.fecha) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.post(`${API}/abonos`, {
        clinica_id: selectedClinica,
        monto: parseFloat(abonoForm.monto),
        tipo: abonoForm.tipo,
        referencia: abonoForm.referencia,
        fecha: abonoForm.fecha
      }, { headers });
      
      toast.success('Abono registrado exitosamente');
      setShowAbonoDialog(false);
      setShowPreviewDialog(false);
      setAbonoForm({ monto: '', tipo: 'transferencia', referencia: '', fecha: new Date().toISOString().split('T')[0] });
      fetchEstadoCuenta(selectedClinica);
      fetchAntiguedadSaldos();
    } catch (error) {
      console.error('Error saving abono:', error);
      toast.error('Error al registrar abono');
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const resetAbonoForm = () => {
    setAbonoForm({ monto: '', tipo: 'transferencia', referencia: '', fecha: new Date().toISOString().split('T')[0] });
    setFifoPreview(null);
  };

  const openAnularDialog = (abono) => {
    setAbonoToAnular(abono);
    setAnularMotivo('');
    setShowAnularDialog(true);
  };

  const handleAnularAbono = async () => {
    if (!abonoToAnular || !anularMotivo.trim()) {
      toast.error('Debe ingresar un motivo de anulación');
      return;
    }
    
    setIsAnulando(true);
    try {
      await axios.put(`${API}/abonos/${abonoToAnular.abono_id}/anular`, {
        motivo: anularMotivo
      }, { headers });
      
      toast.success('Abono anulado exitosamente');
      setShowAnularDialog(false);
      setAbonoToAnular(null);
      setAnularMotivo('');
      fetchEstadoCuenta(selectedClinica);
      fetchAntiguedadSaldos();
    } catch (error) {
      console.error('Error anulando abono:', error);
      const errorMsg = error.response?.data?.detail || 'Error al anular abono';
      toast.error(errorMsg);
    } finally {
      setIsAnulando(false);
    }
  };

  const handleAnularAjuste = async () => {
    if (!ajusteToAnular || !anularAjusteMotivo.trim()) {
      toast.error('Debe ingresar un motivo de anulación');
      return;
    }
    
    setIsAnulandoAjuste(true);
    try {
      await axios.put(`${API}/ajustes/${ajusteToAnular.ajuste_id}/anular`, {
        motivo: anularAjusteMotivo
      }, { headers });
      
      toast.success('Ajuste anulado exitosamente - Se restauró el monto original');
      setShowAnularAjusteDialog(false);
      setAjusteToAnular(null);
      setAnularAjusteMotivo('');
      fetchEstadoCuenta(selectedClinica);
      fetchAntiguedadSaldos();
    } catch (error) {
      console.error('Error anulando ajuste:', error);
      const errorMsg = error.response?.data?.detail || 'Error al anular ajuste';
      toast.error(errorMsg);
    } finally {
      setIsAnulandoAjuste(false);
    }
  };

  const handleExportEstadoCuenta = async (formato) => {
    if (!selectedClinica) {
      toast.error('Seleccione una clínica primero');
      return;
    }
    
    try {
      let url = `${API}/reportes/estado-cuenta/${selectedClinica}/export?formato=${formato}`;
      if (fechaInicio) url += `&fecha_inicio=${fechaInicio}`;
      if (fechaFin) url += `&fecha_fin=${fechaFin}`;
      
      const response = await axios.get(url, {
        headers,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `estado_cuenta_${formato === 'pdf' ? '.pdf' : '.xlsx'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Reporte exportado como ${formato.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar el reporte');
    }
  };

  const handleExportCargosDetallado = async () => {
    if (!selectedClinica) {
      toast.error('Seleccione una clínica primero');
      return;
    }
    try {
      let url = `${API}/reportes/estado-cuenta/${selectedClinica}/export-detallado`;
      const params = [];
      if (fechaInicio) params.push(`fecha_inicio=${fechaInicio}`);
      if (fechaFin) params.push(`fecha_fin=${fechaFin}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url, {
        headers,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `cargos_detallado_${selectedClinica}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Reporte de cargos detallado exportado');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar el reporte de cargos detallado');
    }
  };

  const handleExportAntiguedad = async (formato) => {
    try {
      const response = await axios.get(`${API}/reportes/antiguedad-saldos/export?formato=${formato}`, {
        headers,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `antiguedad_saldos${formato === 'pdf' ? '.pdf' : '.xlsx'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Reporte exportado como ${formato.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar el reporte');
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/dashboard')}
                className="h-10 border-slate-300"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-slate-900">Reportes Financieros</h1>
                <p className="text-sm text-slate-500">Control de pagos y deudas de clínicas</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 p-4 sm:p-6 lg:p-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2 bg-white border border-slate-200">
            <TabsTrigger value="estado-cuenta" className="data-[state=active]:bg-[#505BB3] data-[state=active]:text-white" data-testid="tab-estado-cuenta">
              <Receipt className="w-4 h-4 mr-2" />
              Estado de Cuenta
            </TabsTrigger>
            <TabsTrigger value="antiguedad" className="data-[state=active]:bg-[#505BB3] data-[state=active]:text-white" data-testid="tab-antiguedad">
              <Clock className="w-4 h-4 mr-2" />
              Antigüedad de Saldos
            </TabsTrigger>
          </TabsList>

          {/* Estado de Cuenta Tab */}
          <TabsContent value="estado-cuenta" className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Clínica</Label>
                  <Select value={selectedClinica} onValueChange={handleClinicaChange}>
                    <SelectTrigger className="h-10" data-testid="clinica-select">
                      <SelectValue placeholder="Seleccione una clínica" />
                    </SelectTrigger>
                    <SelectContent>
                      {clinicas.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Desde</Label>
                  <Input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className="h-10"
                    data-testid="fecha-inicio"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Hasta</Label>
                  <Input
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    className="h-10"
                    data-testid="fecha-fin"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    onClick={() => fetchEstadoCuenta(selectedClinica)}
                    disabled={!selectedClinica || isLoading}
                    className="h-10 bg-[#505BB3] hover:bg-[#404A9C] text-white"
                    data-testid="aplicar-filtros"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
                  </Button>
                  {canRegistrarAbono() && (
                    <Button
                      onClick={() => setShowAbonoDialog(true)}
                      disabled={!selectedClinica}
                      className="h-10 bg-green-600 hover:bg-green-700 text-white"
                      data-testid="registrar-abono"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Abono
                    </Button>
                  )}
                </div>
              </div>
              {/* Export buttons */}
              {estadoCuenta && canExport() && (
                <div className="flex items-center gap-2 pt-3 border-t border-slate-200 mt-3">
                  <span className="text-sm text-slate-500">Exportar:</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportEstadoCuenta('pdf')}
                    className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="export-pdf-estado"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportEstadoCuenta('excel')}
                    className="h-8 text-green-600 border-green-200 hover:bg-green-50"
                    data-testid="export-excel-estado"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" />
                    Excel
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportCargosDetallado}
                    className="h-8 text-purple-600 border-purple-200 hover:bg-purple-50"
                    data-testid="export-cargos-detallado"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" />
                    Cargos Detallado
                  </Button>
                </div>
              )}
            </div>

            {/* Estado de Cuenta Display */}
            {estadoCuenta && (
              <>
                {/* Summary Cards */}
                <div className={`grid grid-cols-1 md:grid-cols-${estadoCuenta.clinica.limite_credito_habilitado ? '5' : '4'} gap-4`}>
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Cargos</p>
                        <p className="text-lg font-bold text-red-600">{formatCurrency(estadoCuenta.resumen.total_cargos)}</p>
                      </div>
                    </div>
                  </div>
                  {/* Total Descuentos - solo mostrar si hay descuentos */}
                  {(estadoCuenta.resumen.total_descuentos > 0) && (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Receipt className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Total Descuentos</p>
                          <p className="text-lg font-bold text-purple-600">-{formatCurrency(estadoCuenta.resumen.total_descuentos)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <TrendingDown className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Abonos</p>
                        <p className="text-lg font-bold text-green-600">{formatCurrency(estadoCuenta.resumen.total_abonos)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Saldo Pendiente</p>
                        <p className="text-lg font-bold text-amber-600">{formatCurrency(estadoCuenta.resumen.saldo_pendiente)}</p>
                      </div>
                    </div>
                  </div>
                  {estadoCuenta.clinica.limite_credito_habilitado && (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Límite de Crédito</p>
                          <p className="text-lg font-bold text-blue-600">{formatCurrency(estadoCuenta.clinica.limite_credito)}</p>
                          <p className="text-xs text-slate-400">Usado: {estadoCuenta.resumen.porcentaje_credito.toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Status Summary */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm"><strong>{estadoCuenta.conteo.pagadas}</strong> Pagadas</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      <span className="text-sm"><strong>{estadoCuenta.conteo.parciales}</strong> Parciales</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-red-500" />
                      <span className="text-sm"><strong>{estadoCuenta.conteo.pendientes}</strong> Pendientes</span>
                    </div>
                  </div>
                </div>

                {/* Movements Table */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="bg-[#505BB3] px-4 py-3">
                    <div className="flex items-center gap-2 text-white">
                      <FileText className="w-5 h-5" />
                      <span className="font-semibold">Movimientos</span>
                      <span className="ml-auto text-sm bg-white/20 px-2 py-0.5 rounded-full">
                        {estadoCuenta.movimientos.length}
                      </span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Fecha</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Tipo</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Concepto (ID)</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Paciente</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Especie</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Médico</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Cargo</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Abono</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Estado</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {estadoCuenta.movimientos.map((mov, idx) => (
                          <tr key={idx} className={`hover:bg-slate-50 ${mov.anulado ? 'bg-red-50 opacity-60' : ''} ${mov.tipo === 'ajuste' ? 'bg-purple-50' : ''}`}>
                            <td className="px-3 py-3 text-sm text-slate-600">{mov.fecha}</td>
                            <td className="px-3 py-3">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                mov.anulado ? 'bg-red-100 text-red-700' :
                                mov.tipo === 'solicitud' ? 'bg-blue-100 text-blue-700' :
                                mov.tipo === 'abono' ? 'bg-green-100 text-green-700' :
                                'bg-purple-100 text-purple-700'
                              }`}>
                                {mov.anulado ? 'Anulado' : mov.tipo === 'solicitud' ? 'Solicitud' : mov.tipo === 'abono' ? 'Abono' : 'Descuento'}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-sm text-slate-900 font-mono">
                              {mov.anulado ? (
                                <span className="line-through text-slate-400">{mov.concepto}</span>
                              ) : mov.concepto}
                              {mov.anulado && mov.anulado_motivo && (
                                <span className="block text-xs text-red-500 mt-0.5">
                                  ❌ {mov.anulado_motivo}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-sm text-slate-600">{mov.paciente || '-'}</td>
                            <td className="px-3 py-3 text-sm text-slate-600">{mov.especie || '-'}</td>
                            <td className="px-3 py-3 text-sm text-slate-600">{mov.medico || '-'}</td>
                            <td className={`px-3 py-3 text-sm text-right font-medium ${
                              mov.anulado ? 'text-slate-400 line-through' : 
                              mov.cargo < 0 ? 'text-purple-600' : 
                              mov.cargo > 0 ? 'text-red-600' : 'text-slate-400'
                            }`}>
                              {mov.cargo !== 0 ? formatCurrency(mov.cargo) : '-'}
                            </td>
                            <td className={`px-3 py-3 text-sm text-right font-medium ${mov.anulado ? 'text-slate-400 line-through' : 'text-green-600'}`}>
                              {mov.abono > 0 ? formatCurrency(mov.abono) : '-'}
                            </td>
                            <td className="px-3 py-3 text-center">
                              {mov.anulado ? (
                                <XCircle className="w-5 h-5 text-red-500 inline" />
                              ) : mov.estado === '✅' ? (
                                <CheckCircle className="w-5 h-5 text-green-500 inline" />
                              ) : mov.estado === '⚠️' ? (
                                <AlertTriangle className="w-5 h-5 text-amber-500 inline" />
                              ) : mov.estado === '❌' ? (
                                <Clock className="w-5 h-5 text-red-500 inline" />
                              ) : (
                                <span className="text-lg">{mov.estado}</span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-center">
                              {mov.tipo === 'solicitud' && mov.solicitud_id && (
                                <a
                                  href={`/admin/solicitud/${mov.solicitud_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-blue-100 transition-colors"
                                  title="Ver solicitud (nueva pestaña)"
                                  data-testid={`ver-solicitud-${idx}`}
                                >
                                  <ExternalLink className="w-4 h-4 text-blue-500" />
                                </a>
                              )}
                              {mov.tipo === 'ajuste' && mov.ajuste_detalle && !mov.anulado && (
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 hover:bg-purple-100"
                                    onClick={() => {
                                      setAjusteDetail(mov.ajuste_detalle);
                                      setShowAjusteDialog(true);
                                    }}
                                    title="Ver detalle del ajuste"
                                    data-testid={`ver-ajuste-${idx}`}
                                  >
                                    <FileText className="w-4 h-4 text-purple-600" />
                                  </Button>
                                  {canAnularAjuste() && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 hover:bg-red-100"
                                      onClick={() => {
                                        setAjusteToAnular(mov);
                                        setShowAnularAjusteDialog(true);
                                      }}
                                      title="Anular ajuste"
                                      data-testid={`anular-ajuste-${idx}`}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  )}
                                </div>
                              )}
                              {mov.tipo === 'ajuste' && mov.anulado && (
                                <span className="text-xs text-red-500">Anulado</span>
                              )}
                              {mov.tipo === 'abono' && !mov.anulado && mov.abono_id && canAnularAbono() && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-red-100"
                                  onClick={() => openAnularDialog(mov)}
                                  title="Anular abono"
                                  data-testid={`anular-abono-${idx}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {!selectedClinica && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Seleccione una clínica para ver su estado de cuenta</p>
              </div>
            )}
          </TabsContent>

          {/* Antigüedad de Saldos Tab */}
          <TabsContent value="antiguedad" className="space-y-6">
            {antiguedadSaldos && (
              <>
                {/* Export buttons */}
                {canExport() && (
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Exportar Reporte:</span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExportAntiguedad('pdf')}
                          className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                          data-testid="export-pdf-antiguedad"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExportAntiguedad('excel')}
                          className="h-8 text-green-600 border-green-200 hover:bg-green-50"
                          data-testid="export-excel-antiguedad"
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-1" />
                          Excel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Totals Summary */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <p className="text-xs text-slate-500 uppercase font-medium">0-30 días</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(antiguedadSaldos.totales.corriente_0_30)}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <p className="text-xs text-slate-500 uppercase font-medium">31-60 días</p>
                    <p className="text-lg font-bold text-amber-600">{formatCurrency(antiguedadSaldos.totales.dias_31_60)}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <p className="text-xs text-slate-500 uppercase font-medium">61-90 días</p>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(antiguedadSaldos.totales.dias_61_90)}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <p className="text-xs text-slate-500 uppercase font-medium">+90 días</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(antiguedadSaldos.totales.dias_90_plus)}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <p className="text-xs text-slate-500 uppercase font-medium">Total</p>
                    <p className="text-lg font-bold text-slate-900">{formatCurrency(antiguedadSaldos.totales.total)}</p>
                  </div>
                </div>

                {/* Clinics Table */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="bg-[#505BB3] px-4 py-3">
                    <div className="flex items-center gap-2 text-white">
                      <Clock className="w-5 h-5" />
                      <span className="font-semibold">Antigüedad de Saldos por Clínica</span>
                      <span className="ml-auto text-sm">Fecha: {antiguedadSaldos.fecha_reporte}</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Riesgo</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Clínica</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">0-30</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">31-60</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">61-90</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">+90</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Total</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Límite</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {antiguedadSaldos.clinicas.map((clinica) => (
                          <tr key={clinica.clinica_id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-center text-lg">{clinica.riesgo}</td>
                            <td className="px-4 py-3 text-sm font-medium text-slate-900">{clinica.clinica_nombre}</td>
                            <td className="px-4 py-3 text-sm text-right text-green-600">{formatCurrency(clinica.corriente_0_30)}</td>
                            <td className="px-4 py-3 text-sm text-right text-amber-600">{formatCurrency(clinica.dias_31_60)}</td>
                            <td className="px-4 py-3 text-sm text-right text-orange-600">{formatCurrency(clinica.dias_61_90)}</td>
                            <td className="px-4 py-3 text-sm text-right text-red-600">{formatCurrency(clinica.dias_90_plus)}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-slate-900">{formatCurrency(clinica.total)}</td>
                            <td className="px-4 py-3 text-sm text-right text-slate-500">
                              {clinica.limite_credito ? formatCurrency(clinica.limite_credito) : '-'}
                            </td>
                          </tr>
                        ))}
                        {antiguedadSaldos.clinicas.length === 0 && (
                          <tr>
                            <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                              No hay clínicas con saldo pendiente
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {antiguedadSaldos.clinicas.length > 0 && (
                        <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                          <tr>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-sm font-bold text-slate-900">TOTALES</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-green-600">{formatCurrency(antiguedadSaldos.totales.corriente_0_30)}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-amber-600">{formatCurrency(antiguedadSaldos.totales.dias_31_60)}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-orange-600">{formatCurrency(antiguedadSaldos.totales.dias_61_90)}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-red-600">{formatCurrency(antiguedadSaldos.totales.dias_90_plus)}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-slate-900">{formatCurrency(antiguedadSaldos.totales.total)}</td>
                            <td className="px-4 py-3"></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </>
            )}

            {isLoading && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                <Loader2 className="w-8 h-8 text-[#505BB3] animate-spin mx-auto" />
                <p className="text-slate-500 mt-4">Cargando reporte...</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Abono Dialog */}
      <Dialog open={showAbonoDialog} onOpenChange={(open) => { if (!open) resetAbonoForm(); setShowAbonoDialog(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" />
              Registrar Abono
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Saldo Pendiente de la Clínica */}
            {estadoCuenta && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-amber-700 font-medium">Saldo Pendiente:</span>
                  <span className="text-lg font-bold text-amber-700" data-testid="saldo-pendiente-modal">
                    {formatCurrency(estadoCuenta.resumen.saldo_pendiente)}
                  </span>
                </div>
                <p className="text-xs text-amber-600 mt-1">Clínica: {estadoCuenta.clinica.nombre}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Monto *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={abonoForm.monto}
                  onChange={(e) => setAbonoForm({ ...abonoForm, monto: e.target.value })}
                  placeholder="0.00"
                  className="pl-9 h-11"
                  data-testid="abono-monto"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Tipo de Pago</Label>
              <Select value={abonoForm.tipo} onValueChange={(value) => setAbonoForm({ ...abonoForm, tipo: value })}>
                <SelectTrigger className="h-11" data-testid="abono-tipo">
                  <SelectValue placeholder="Seleccione tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">💳 Transferencia</SelectItem>
                  <SelectItem value="efectivo">💵 Efectivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Referencia / Nota</Label>
              <Input
                value={abonoForm.referencia}
                onChange={(e) => setAbonoForm({ ...abonoForm, referencia: e.target.value })}
                placeholder="Ej: Transferencia BBVA #12345"
                className="h-11"
                data-testid="abono-referencia"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Fecha de Pago *</Label>
              <Input
                type="date"
                value={abonoForm.fecha}
                onChange={(e) => setAbonoForm({ ...abonoForm, fecha: e.target.value })}
                className="h-11"
                data-testid="abono-fecha"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetAbonoForm(); setShowAbonoDialog(false); }} className="border-slate-300">
              Cancelar
            </Button>
            <Button onClick={handlePreviewAbono} disabled={!abonoForm.monto || isLoading} className="bg-[#505BB3] hover:bg-[#404A9C] text-white" data-testid="preview-abono">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Vista Previa FIFO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FIFO Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">Vista Previa de Aplicación FIFO</DialogTitle>
          </DialogHeader>
          {fifoPreview && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-green-600 uppercase font-medium">Monto Abono</p>
                  <p className="text-lg font-bold text-green-700">{formatCurrency(parseFloat(abonoForm.monto))}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-blue-600 uppercase font-medium">Se Aplicará</p>
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(fifoPreview.monto_total_aplicado)}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-amber-600 uppercase font-medium">Sobrante</p>
                  <p className="text-lg font-bold text-amber-700">{formatCurrency(fifoPreview.saldo_restante)}</p>
                </div>
              </div>

              {fifoPreview.solicitudes_afectadas.length > 0 ? (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">ID</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Paciente</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-600">Debe</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-600">Se Aplica</th>
                        <th className="px-3 py-2 text-center font-medium text-slate-600">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {fifoPreview.solicitudes_afectadas.map((sol, idx) => (
                        <tr key={idx} className={sol.se_aplica > 0 ? 'bg-green-50' : ''}>
                          <td className="px-3 py-2 font-mono text-xs">{sol.id_corto}</td>
                          <td className="px-3 py-2">{sol.paciente}</td>
                          <td className="px-3 py-2 text-right text-red-600">{formatCurrency(sol.debe)}</td>
                          <td className="px-3 py-2 text-right text-green-600 font-medium">{formatCurrency(sol.se_aplica)}</td>
                          <td className="px-3 py-2 text-center">{sol.estado_nuevo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  No hay solicitudes pendientes para aplicar este abono
                </div>
              )}

              {fifoPreview.saldo_restante > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-700">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Nota: {formatCurrency(fifoPreview.saldo_restante)} quedará como saldo a favor para futuras solicitudes.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)} className="border-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleSaveAbono} disabled={isSaving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirmar-abono">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Confirmar y Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anular Abono Dialog */}
      <Dialog open={showAnularDialog} onOpenChange={(open) => { if (!open) { setAbonoToAnular(null); setAnularMotivo(''); } setShowAnularDialog(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-600 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Anular Abono
            </DialogTitle>
          </DialogHeader>
          {abonoToAnular && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                ¿Está seguro de anular este abono? Esta acción revertirá los pagos aplicados a las solicitudes afectadas.
              </p>
              
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Monto:</span>
                  <span className="font-semibold text-slate-900">{formatCurrency(abonoToAnular.abono)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Fecha:</span>
                  <span className="text-slate-900">{abonoToAnular.fecha}</span>
                </div>
                {abonoToAnular.concepto && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Referencia:</span>
                    <span className="text-slate-900">{abonoToAnular.concepto}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Motivo de anulación *</Label>
                <Textarea
                  value={anularMotivo}
                  onChange={(e) => setAnularMotivo(e.target.value)}
                  placeholder="Ej: Error en el monto registrado, abono duplicado, etc."
                  className="min-h-[80px] border-slate-300"
                  data-testid="anular-motivo"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  El abono quedará registrado como "Anulado" para fines de auditoría.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAnularDialog(false); setAbonoToAnular(null); setAnularMotivo(''); }} className="border-slate-300">
              Cancelar
            </Button>
            <Button 
              onClick={handleAnularAbono} 
              disabled={isAnulando || !anularMotivo.trim()} 
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirmar-anular"
            >
              {isAnulando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
              Confirmar Anulación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ajuste Detail Dialog */}
      <Dialog open={showAjusteDialog} onOpenChange={(open) => { if (!open) setAjusteDetail(null); setShowAjusteDialog(open); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-purple-600 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Detalle del Ajuste
              {ajusteDetail?.id_corto && (
                <span className="ml-2 text-sm font-mono bg-purple-100 px-2 py-0.5 rounded">{ajusteDetail.id_corto}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {ajusteDetail && (
            <div className="space-y-4 py-4">
              {/* Montos */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 uppercase font-medium">Monto Original</p>
                  <p className="text-lg font-bold text-slate-900">{formatCurrency(ajusteDetail.monto_original)}</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-purple-600 uppercase font-medium">Descuento</p>
                  <p className="text-lg font-bold text-purple-700">-{formatCurrency(ajusteDetail.monto_diferencia)}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-blue-600 uppercase font-medium">Monto Ajustado</p>
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(ajusteDetail.monto_ajustado)}</p>
                </div>
              </div>

              {/* Motivo */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-600 uppercase font-medium mb-1">Motivo del Ajuste</p>
                <p className="text-sm text-amber-800">{ajusteDetail.motivo || 'Sin motivo especificado'}</p>
              </div>

              {/* Pruebas Removidas */}
              {ajusteDetail.pruebas_removidas && ajusteDetail.pruebas_removidas.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-3 py-2 border-b border-slate-200">
                    <p className="text-xs font-semibold text-slate-600 uppercase">Pruebas Removidas ({ajusteDetail.pruebas_removidas.length})</p>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Prueba</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Precio</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {ajusteDetail.pruebas_removidas.map((prueba, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2 text-slate-700">
                              {prueba.nombre}
                              {prueba.es_escalonado && (
                                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${prueba.es_primera ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                                  {prueba.es_primera ? 'Base' : 'Adicional'}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-red-600">{formatCurrency(prueba.precio)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="text-xs text-slate-400 pt-2 border-t border-slate-200">
                <div className="flex justify-between">
                  <span>Fecha del ajuste:</span>
                  <span>{ajusteDetail.created_at ? new Date(ajusteDetail.created_at).toLocaleString('es-MX') : 'N/A'}</span>
                </div>
                {ajusteDetail.created_by && (
                  <div className="flex justify-between mt-1">
                    <span>Realizado por:</span>
                    <span>{ajusteDetail.created_by}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAjusteDialog(false); setAjusteDetail(null); }} className="border-slate-300">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anular Ajuste Dialog */}
      <Dialog open={showAnularAjusteDialog} onOpenChange={(open) => { if (!open) { setAjusteToAnular(null); setAnularAjusteMotivo(''); } setShowAnularAjusteDialog(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-600 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Anular Ajuste (Descuento)
            </DialogTitle>
          </DialogHeader>
          {ajusteToAnular && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                ¿Está seguro de anular este ajuste? Esta acción restaurará el monto original de la solicitud.
              </p>
              
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Solicitud:</span>
                  <span className="font-mono font-semibold text-slate-900">{ajusteToAnular.concepto}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Descuento a anular:</span>
                  <span className="font-semibold text-purple-600">{formatCurrency(Math.abs(ajusteToAnular.cargo))}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Fecha:</span>
                  <span className="text-slate-900">{ajusteToAnular.fecha}</span>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Al anular, se restaurarán las pruebas removidas y el monto original de la solicitud.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Motivo de anulación *</Label>
                <Textarea
                  value={anularAjusteMotivo}
                  onChange={(e) => setAnularAjusteMotivo(e.target.value)}
                  placeholder="Ej: Se decidió no aplicar el descuento, error en el ajuste, etc."
                  className="min-h-[80px] border-slate-300"
                  data-testid="anular-ajuste-motivo"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAnularAjusteDialog(false); setAjusteToAnular(null); setAnularAjusteMotivo(''); }} className="border-slate-300">
              Cancelar
            </Button>
            <Button 
              onClick={handleAnularAjuste} 
              disabled={isAnulandoAjuste || !anularAjusteMotivo.trim()} 
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirmar-anular-ajuste"
            >
              {isAnulandoAjuste ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
              Confirmar Anulación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
